import { validateOwnerData, fetchOwnerData, validateProspectiveData } from 'registration/actions/validate-owner-data.actions';
import { fetchOwnerValidationMethod } from 'registration/actions/fetch-owner-validation.actions';
import moment from 'moment/moment';
import { updateRetailerDetails } from 'registration/actions/fetch-retailer-details.actions';
import template from './pii.html';

const mapStateToThis = ({ session, registration, config }) => {
  const { locale } = session;
  const { ownerData, ownerValidationMethod, retailer } = registration;
  const {
    phoneMask,
    phoneLength,
    maxFirstNameLength,
    maxLastNameLength,
    maxEmailLength,
    retailerManagerApiDateFormat,
    userValidationDateFormat,
    registrationLockoutPeriodMinutes,
  } = config;

  const ownerValidationMethods = ownerValidationMethod[locale] ? ownerValidationMethod[locale] : [];
  const hasOwnerValidationMethods = ownerValidationMethods && ownerValidationMethods.length !== 0;

  return {
    ownerValidationMethods,
    hasOwnerValidationMethods,
    retailer,
    ownerData,
    phoneMask,
    phoneLength,
    maxFirstNameLength,
    maxLastNameLength,
    maxEmailLength,
    retailerManagerApiDateFormat,
    userValidationDateFormat,
    registrationLockoutPeriodMinutes,
  };
};

const mapDispatchToThis = {
  fetchOwnerValidationMethod,
  fetchOwnerData,
  validateOwnerData,
  validateProspectiveData,
  updateRetailerDetails,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', '$state', 'Notifications', 'gettextCatalog', '$scope')
class SelfRegistrationPiiController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
  }

  async $onInit() {
    this.showCaptcha = true;
    this.$scope.$on('validatePII', () => this.validateOwner(this.form));
    await this.fetchOwnerValidationMethod(this.retailerType || this.retailer.retailerType, this.retailer.outletTypeId);
    this.pinLabel = this.ownerValidationMethods.map((ownerValidationMethod) => ownerValidationMethod.label).join(', ');
  }

  $onDestroy() {
    this.unsubscribe();
  }

  validatePiiField(form) {
    if (!this.showButton) {
      this.validateOwner(form);
    } else {
      this.showCaptcha = true;
    }
  }

  async validatePiiForm(form) {
    this.form.$setSubmitted();

    if (
      (this.form.captchaToken && this.form.captchaToken.$invalid) ||
      (this.form.userValidation1 && this.form.userValidation1.$invalid) ||
      (this.form.userValidation2 && this.form.userValidation2.$invalid) ||
      (this.form.userValidation3 && this.form.userValidation3.$invalid)
    ) {
      return;
    }

    const isFormValid = this.isProspective ? await this.isProspectiveFormValid(form) : await this.isOwnerFormValid(form);

    if (isFormValid) {
      this.showCaptcha = false;
      this.form.$setPristine();
      this.updateRetailerDetails({ canSelfRegister: true });
    }
  }

  async isOwnerFormValid(form) {
    await this.fetchOwner(form);

    if (!this.ownerData.allow) {
      return false;
    }
    if (this.ownerData.exist) {
      await this.Notifications.showAlert(
        this.gettextCatalog.getString('Owner information in use.'),
        this.gettextCatalog.getString('The email address associated with this owner is already in use. Please contact the lottery for further assistance.')
      ).result;
      this.$state.go('home-view');
      return false;
    }

    if (!this.ownerData.owner) {
      await this.Notifications.showAlert(
        this.gettextCatalog.getString('No owner found'),
        this.gettextCatalog.getString('No owner found matching personal information. Prospective Retailer account will be created.')
      ).result;
    } else if (this.ownerData.owner && this.ownerData.isProspective) {
      await this.Notifications.showAlert(
        this.gettextCatalog.getString('No active retailers found'),
        this.gettextCatalog.getString('No active retailers found for the entered data. A Prospective Retailer account will be created.')
      ).result;
    } else {
      this.formModel.firstName = this.ownerData.owner.firstName;
      this.formModel.lastName = this.ownerData.owner.lastName;
      this.formModel.emailAddress = this.ownerData.owner.emailAddress;
      this.formModel.confirmEmail = this.ownerData.owner.emailAddress;
      this.formModel.mobilePhone = this.ownerData.owner.mobilePhone;
      this.formModel.businessPhone = this.ownerData.owner.workPhone;
    }
    return true;
  }

  async isProspectiveFormValid(form) {
    await this.validateProspective(form);
    return this.ownerData.validPin;
  }

  async validateOwner(form) {
    const {
      userValidation1 = { $modelValue: '' },
      userValidation2 = { $modelValue: '' },
      userValidation3 = { $modelValue: '' },
    } = this.form;

    const userValidation1Value =
      userValidation1.$modelValue instanceof Date
        ? moment(userValidation1.$modelValue).utc().format(this.userValidationDateFormat)
        : userValidation1.$modelValue || '';
    const userValidation2Value =
      userValidation2.$modelValue instanceof Date
        ? moment(userValidation2.$modelValue).utc().format(this.userValidationDateFormat)
        : userValidation2.$modelValue || '';
    const userValidation3Value =
      userValidation3.$modelValue instanceof Date
        ? moment(userValidation3.$modelValue).utc().format(this.userValidationDateFormat)
        : userValidation3.$modelValue || '';

    this.formModel.userValidation = userValidation1Value + userValidation2Value + userValidation3Value;

    if (
      (form.userValidation1 && form.userValidation1.$invalid) ||
      (form.userValidation2 && form.userValidation2.$invalid) ||
      (form.userValidation3 && form.userValidation3.$invalid)
    ) {
      return;
    }

    this.isLoading = true;
    form.userValidation.$setValidity('isLoading', false);
    await this.validateOwnerData(this.formModel, this.retailerType);
    this.isLoading = false;
    form.userValidation.$setValidity('isLoading', true);

    form.userValidation.$setValidity('blocked', !this.ownerData.blocked);
    form.userValidation.$setValidity('validPin', this.ownerData.validPin);
    form.userValidation.$setValidity('isPinUsed', !this.ownerData.isPinUsed);
    form.userValidation.$setValidity('pinIsValidForRetailerType', this.ownerData.pinIsValidForRetailerType);
    form.userValidation.$setValidity('noRetailerIdProvided', !this.ownerData.noRetailerIdProvided);
  }

  async validateProspective(form) {
    const {
      userValidation1 = { $modelValue: '' },
      userValidation2 = { $modelValue: '' },
      userValidation3 = { $modelValue: '' },
    } = this.form;

    const userValidation1Value =
      userValidation1.$modelValue instanceof Date
        ? moment(userValidation1.$modelValue).utc().format(this.userValidationDateFormat)
        : userValidation1.$modelValue || '';
    const userValidation2Value =
      userValidation2.$modelValue instanceof Date
        ? moment(userValidation2.$modelValue).utc().format(this.userValidationDateFormat)
        : userValidation2.$modelValue || '';
    const userValidation3Value =
      userValidation3.$modelValue instanceof Date
        ? moment(userValidation3.$modelValue).utc().format(this.userValidationDateFormat)
        : userValidation3.$modelValue || '';

    this.formModel.userValidation = userValidation1Value + userValidation2Value + userValidation3Value;

    if (
      (form.userValidation1 && form.userValidation1.$invalid) ||
      (form.userValidation2 && form.userValidation2.$invalid) ||
      (form.userValidation3 && form.userValidation3.$invalid)
    ) {
      return;
    }

    this.isLoading = true;
    form.userValidation.$setValidity('isLoading', false);
    await this.validateProspectiveData(this.formModel);
    this.isLoading = false;
    form.userValidation.$setValidity('isLoading', true);
    form.userValidation.$setValidity('validPin', this.ownerData.validPin);
  }

  async fetchOwner(form) {
    const {
      userValidation1 = { $modelValue: '' },
      userValidation2 = { $modelValue: '' },
      userValidation3 = { $modelValue: '' },
    } = this.form;

    const userValidation1Value =
      userValidation1.$modelValue instanceof Date
        ? moment(userValidation1.$modelValue).utc().format(this.retailerManagerApiDateFormat)
        : userValidation1.$modelValue || '';
    const userValidation2Value =
      userValidation2.$modelValue instanceof Date
        ? moment(userValidation2.$modelValue).utc().format(this.retailerManagerApiDateFormat)
        : userValidation2.$modelValue || '';
    const userValidation3Value =
      userValidation3.$modelValue instanceof Date
        ? moment(userValidation3.$modelValue).utc().format(this.retailerManagerApiDateFormat)
        : userValidation3.$modelValue || '';

    let ownerUrlParams = '';
    this.ownerValidationMethods.forEach((validationMethod) => {
      // eslint-disable-next-line no-eval
      ownerUrlParams = `${ownerUrlParams + validationMethod.id}=${eval(`userValidation${validationMethod.ordinal}Value`)}&`;
    });

    this.formModel.userValidation = ownerUrlParams;

    if (
      (form.userValidation1 && form.userValidation1.$invalid) ||
      (form.userValidation2 && form.userValidation2.$invalid) ||
      (form.userValidation3 && form.userValidation3.$invalid)
    ) {
      return;
    }

    this.isLoading = true;
    form.userValidation.$setValidity('isLoading', false);
    await this.fetchOwnerData(this.formModel);
    form.userValidation.$setValidity('isLoading', true);
    this.isLoading = false;

    form.userValidation.$setValidity('allow', this.ownerData.allow);
  }
}

export default class SelfRegistrationPiiComponent {
  constructor() {
    this.template = template;
    this.controller = SelfRegistrationPiiController;
    this.bindings = {
      form: '=',
      formModel: '=',
      showButton: '<',
      isProspective: '<',
      retailerType: '<',
    };
    return this;
  }
}



// WEBPACK FOOTER //
// ./src/modules/registration/components/self-registration/pii/pii-component.js